import { useQuery, useMutation } from 'react-query';

import { useCurrentAddressInfo } from '@/hooks';
import { queryClient, request, toastError } from '@/utils';

import { AccountHistoryRecord } from '../types';

export const useL2WithdrawFee = (chain_id?: string, token_id?: string) => {
  return useQuery(
    ['/wallet/withdraw_fee', chain_id, token_id],
    () =>
      request
        .get<{ fee: number }>('/wallet/withdraw_fee', {
          chain_id: chain_id!,
          token_id: token_id!,
        })
        .then(res => res?.fee),
    {
      enabled: !!chain_id && !!token_id,
    },
  );
};

export const useWithdrawL2 = () => {
  const mutation = useMutation(
    (tx: {
      action: {
        name: string;
        amount: number;
        fee: number;
      };
      nonce: number;
      message: string;
      user_addr: string;
      r: string;
      s: string;
    }) => request.post<{ action_id: string }>('/wallet/withdraw', { tx }),
    {
      onSuccess: invalidL2History,
      onError(err: string) {
        toastError(err);
      },
    },
  );

  return mutation;
};

export const invalidL2History = () => {
  queryClient.invalidateQueries(['/user/l2_action_list']);
};

export const useCurrentAccountHistory = (
  start: number = 0,
  limit: number = 10,
  type: string = '',
) => {
  const { address: meId } = useCurrentAddressInfo();
  return useQuery(
    ['/user/l2_action_list', meId, start, limit, type],
    async () => {
      const res = await request.get<{
        actions: AccountHistoryRecord[];
        pagination: { start: number; limit: number; total: number };
      }>('/user/l2_action_list', {
        id: meId!,
        start,
        limit,
        type,
      });

      const actions = res.actions.map(action => {
        const isPending = action.status === 'Pending';
        const isDeposit = action.type === 0;
        const isWithdraw = action.type === 2;

        return {
          ...action,
          balanceChange: {
            value: action.balance_change,
            status: action.status,
          },
          statusWithConfirmNumbers: {
            pendingTooltip: isPending
              ? isDeposit
                ? `Your deposit will be completed after ${action.needed_confirm_nums} confirmations`
                : isWithdraw
                  ? 'Your withdrawal is awaiting confirmation on Layer 1'
                  : ''
              : '',
            value: action.status,
            confirmNumbers:
              isPending && action.confirm_nums
                ? `${action.confirm_nums}/${action.needed_confirm_nums}`
                : ``,
          },
        };
      });

      return {
        pagination: res.pagination,
        actions,
      };
    },
    {
      enabled: !!meId,
    },
  );
};
